/**
 * GitHub Import Component
 * 
 * Allows importing and managing GitHub repositories for projects
 * Features: OAuth connect, repo browser, import modal, sync status, disconnect
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Sync as SyncIcon,
  LinkOff as DisconnectIcon,
  Star as StarIcon,
  ForkRight as ForkIcon,
  BugReport as IssueIcon,
  Visibility as WatchersIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
} from '@mui/icons-material';
import GitHubService, { GitHubRepository, GitHubRepoData } from '../../services/GitHubService';

interface GitHubImportProps {
  projectId: number;
}

const GitHubImport: React.FC<GitHubImportProps> = ({ projectId }) => {
  const [repository, setRepository] = useState<GitHubRepository | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // OAuth and repository browsing
  const [accessToken, setAccessToken] = useState<string>('');
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [repositories, setRepositories] = useState<GitHubRepoData[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepoData | null>(null);
  const [importing, setImporting] = useState(false);

  // Sync and disconnect states
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [importingSpecs, setImportingSpecs] = useState(false);

  useEffect(() => {
    loadRepository();
  }, [projectId]);

  const loadRepository = async () => {
    try {
      setLoading(true);
      setError(null);
      const repo = await GitHubService.getProjectRepository(projectId);
      setRepository(repo);
    } catch (err: any) {
      setError(err.message || 'Failed to load repository');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectClick = () => {
    setShowTokenDialog(true);
    setError(null);
  };

  const handleTokenSubmit = async () => {
    if (!accessToken.trim()) {
      setError('Please enter a GitHub access token');
      return;
    }

    try {
      setLoadingRepos(true);
      setError(null);
      const repos = await GitHubService.getUserRepositories(projectId, accessToken);
      setRepositories(repos);
      setShowTokenDialog(false);
      setShowImportDialog(true);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch repositories');
    } finally {
      setLoadingRepos(false);
    }
  };

  const handleImportRepository = async () => {
    if (!selectedRepo) return;

    try {
      setImporting(true);
      setError(null);
      const result = await GitHubService.importRepository(projectId, accessToken, selectedRepo);
      setRepository(result.repository);
      
      // Show specs import result if available
      if (result.specs_import) {
        const specsMsg = result.specs_import.success 
          ? result.specs_import.message 
          : `Note: ${result.specs_import.message}`;
        setSuccess(`Repository imported successfully! ${specsMsg}`);
      } else {
        setSuccess('Repository imported successfully!');
      }
      
      setShowImportDialog(false);
      setSelectedRepo(null);
      setAccessToken('');
    } catch (err: any) {
      setError(err.message || 'Failed to import repository');
    } finally {
      setImporting(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const updated = await GitHubService.syncRepository(projectId);
      setRepository(updated);
      setSuccess('Repository synced successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to sync repository');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect this repository?')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);
      await GitHubService.disconnectRepository(projectId);
      setRepository(null);
      setSuccess('Repository disconnected successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to disconnect repository');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleImportSpecs = async () => {
    try {
      setImportingSpecs(true);
      setError(null);
      const result = await GitHubService.importSpecs(projectId);
      
      // Show detailed information about what was found
      if (result.data?.spec_folder) {
        const filesFound = Object.entries(result.data.files_found || {})
          .filter(([_, path]) => path)
          .map(([type, path]) => `${type}: ${path}`)
          .join(', ');
        
        const message = `${result.message}\n\nFolder: ${result.data.spec_folder}\nFiles: ${filesFound}\n\nProcessing in background - refresh the page in a few moments to see the imported specs.`;
        setSuccess(message);
      } else {
        setSuccess(result.message);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import specifications');
    } finally {
      setImportingSpecs(false);
    }
  };

  const getSyncStatusIcon = () => {
    if (!repository) return null;

    switch (repository.sync_status) {
      case 'synced':
        return <CheckIcon color="success" />;
      case 'syncing':
        return <CircularProgress size={20} />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <PendingIcon color="action" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {!repository ? (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <GitHubIcon fontSize="large" />
              <Box>
                <Typography variant="h6">GitHub Integration</Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect a GitHub repository to sync your project code
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<GitHubIcon />}
              onClick={handleConnectClick}
              fullWidth
            >
              Connect GitHub Repository
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <GitHubIcon />
                  <Typography variant="h6">{repository.full_name}</Typography>
                  {repository.is_private && (
                    <Chip label="Private" size="small" color="default" />
                  )}
                </Box>
                {repository.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {repository.description}
                  </Typography>
                )}
              </Box>
              <Box>
                {getSyncStatusIcon()}
              </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 3 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <StarIcon fontSize="small" color="action" />
                  <Typography variant="body2">{repository.stars_count}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <ForkIcon fontSize="small" color="action" />
                  <Typography variant="body2">{repository.forks_count}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <WatchersIcon fontSize="small" color="action" />
                  <Typography variant="body2">{repository.watchers_count}</Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <IssueIcon fontSize="small" color="action" />
                  <Typography variant="body2">{repository.open_issues_count}</Typography>
                </Box>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last synced: {GitHubService.getRelativeTime(repository.last_synced_at)}
                </Typography>
                {repository.sync_status === 'error' && repository.sync_error && (
                  <Typography variant="caption" color="error" display="block">
                    {repository.sync_error}
                  </Typography>
                )}
              </Box>
              <Chip
                label={GitHubService.formatSyncStatus(repository.sync_status).label}
                color={GitHubService.formatSyncStatus(repository.sync_status).color}
                size="small"
              />
            </Box>
          </CardContent>

          <CardActions>
            <Button
              startIcon={<SyncIcon />}
              onClick={handleSync}
              disabled={syncing || repository.sync_status === 'syncing'}
            >
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            <Button
              startIcon={<GitHubIcon />}
              onClick={handleImportSpecs}
              disabled={importingSpecs}
              color="primary"
              variant="outlined"
            >
              {importingSpecs ? 'Importing Specs...' : 'Import Specs'}
            </Button>
            <Button
              href={repository.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View on GitHub
            </Button>
            <Button
              startIcon={<DisconnectIcon />}
              onClick={handleDisconnect}
              disabled={disconnecting}
              color="error"
            >
              {disconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </CardActions>
        </Card>
      )}

      {/* Token Input Dialog */}
      <Dialog open={showTokenDialog} onClose={() => setShowTokenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Connect GitHub Repository</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter your GitHub Personal Access Token to browse and import repositories.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create a token at{' '}
            <a
              href="https://github.com/settings/tokens/new"
              target="_blank"
              rel="noopener noreferrer"
            >
              github.com/settings/tokens
            </a>{' '}
            with <code>repo</code> scope.
          </Typography>
          <TextField
            fullWidth
            label="GitHub Access Token"
            type="password"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            placeholder="ghp_..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTokenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTokenSubmit}
            disabled={loadingRepos || !accessToken.trim()}
          >
            {loadingRepos ? 'Loading...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Repository Browser Dialog */}
      <Dialog open={showImportDialog} onClose={() => setShowImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Repository</DialogTitle>
        <DialogContent>
          {repositories.length === 0 ? (
            <Alert severity="info">No repositories found</Alert>
          ) : (
            <List>
              {repositories.map((repo, index) => (
                <React.Fragment key={repo.id}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={selectedRepo?.id === repo.id}
                      onClick={() => setSelectedRepo(repo)}
                    >
                      <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {repo.full_name}
                          {repo.private && <Chip label="Private" size="small" />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {repo.description || 'No description'}
                          </Typography>
                          <Box display="flex" gap={2} mt={0.5}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <StarIcon fontSize="small" />
                              <Typography variant="caption">{repo.stargazers_count}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ForkIcon fontSize="small" />
                              <Typography variant="caption">{repo.forks_count}</Typography>
                            </Box>
                            {repo.language && (
                              <Typography variant="caption">• {repo.language}</Typography>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {selectedRepo?.id === repo.id && <CheckIcon color="primary" />}
                    </ListItemSecondaryAction>
                  </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImportDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleImportRepository}
            disabled={!selectedRepo || importing}
          >
            {importing ? 'Importing...' : 'Import Repository'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GitHubImport;
